import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService, { User } from '../services/authService';

/**
 * Get redirect path based on user role
 */
const getRedirectPath = (userType: string): string => {
  switch (userType) {
    case 'admin':
      return '/dashboard/admin';
    case 'employer':
      return '/?mode=employer';
    case 'employee':
      return '/';
    default:
      return '/';
  }
};

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message: string; redirectPath?: string }>;
  googleLogin: (credential: string, userType?: string) => Promise<{ success: boolean; message: string; redirectPath?: string }>;
  register: (data: any) => Promise<{ success: boolean; message: string; redirectPath?: string }>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      
      try {
        if (authService.isAuthenticated()) {
          // Try to get fresh profile data
          const profileResult = await authService.getProfile();
          
          if (profileResult.networkError) {
            // Backend is down - use cached user data
            console.warn('⚠️ Using cached user data - backend unavailable');
            const cachedUser = authService.getCurrentUser();
            if (cachedUser) {
              setUser(cachedUser);
              setIsAuthenticated(true);
            } else {
              setUser(null);
              setIsAuthenticated(false);
            }
          } else if (profileResult.success && profileResult.user) {
            setUser(profileResult.user);
            setIsAuthenticated(true);
          } else {
            // Token is invalid, clear auth state
            console.log('🔒 Token invalid, logging out...');
            await authService.logout();
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          // No stored auth data
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Clear auth state on error
        await authService.logout();
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Listen for token invalidation events from API interceptor
    const handleTokenInvalid = () => {
      console.log('🔒 Token invalidated, updating auth state...');
      setUser(null);
      setIsAuthenticated(false);
    };

    window.addEventListener('auth:token-invalid', handleTokenInvalid);

    return () => {
      window.removeEventListener('auth:token-invalid', handleTokenInvalid);
    };
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; message: string; redirectPath?: string }> => {
    try {
      const result = await authService.login({ email, password });
      
      if (result.success && result.data) {
        setUser(result.data.user);
        setIsAuthenticated(true);
        
        // Determine redirect path based on user role
        const redirectPath = getRedirectPath(result.data.user.user_type);
        
        return { success: true, message: result.message, redirectPath };
      } else {
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Login failed' };
    }
  };

  const googleLogin = async (credential: string, userType?: string): Promise<{ success: boolean; message: string; redirectPath?: string }> => {
    try {
      const result = await authService.googleLogin(credential, userType);
      
      if (result.success && result.data) {
        setUser(result.data.user);
        setIsAuthenticated(true);
        
        const redirectPath = getRedirectPath(result.data.user.user_type);
        return { success: true, message: result.message, redirectPath };
      } else {
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Google login error:', error);
      return { success: false, message: 'Google prisijungimas nepavyko' };
    }
  };

  const register = async (data: any): Promise<{ success: boolean; message: string; redirectPath?: string }> => {
    try {
      const result = await authService.register(data);
      
      if (result.success && result.data) {
        setUser(result.data.user);
        setIsAuthenticated(true);
        
        // Determine redirect path based on user role
        const redirectPath = getRedirectPath(result.data.user.user_type);
        
        return { success: true, message: result.message, redirectPath };
      } else {
        return { success: false, message: result.message || 'Registracija nepavyko' };
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      return { success: false, message: error?.message || 'Registracija nepavyko. Bandykite dar kartą.' };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const refreshProfile = async (): Promise<void> => {
    try {
      const result = await authService.getProfile();
      if (result.success && result.user) {
        setUser(result.user);
      }
    } catch (error) {
      console.error('Profile refresh error:', error);
    }
  };

  // Auto-refresh token periodically (every 6 days for a 7-day token)
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const refreshInterval = setInterval(async () => {
      if (authService.isTokenExpired()) {
        console.log('🔄 Token expiring soon, attempting refresh...');
        const refreshed = await authService.refreshToken();
        if (!refreshed) {
          console.warn('⚠️ Token refresh failed, logging out...');
          await logout();
        }
      }
    }, 6 * 60 * 60 * 1000); // Check every 6 hours

    // Also check immediately on visibility change (user returns to tab)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && authService.isTokenExpired()) {
        console.log('🔄 Tab visible and token expired, attempting refresh...');
        const refreshed = await authService.refreshToken();
        if (!refreshed) {
          console.warn('⚠️ Token refresh failed, logging out...');
          await logout();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    googleLogin,
    register,
    logout,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
